import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  FileText, 
  DollarSign,
  Settings,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

// Import existing page components to use as tab content
import BillingDashboard from './billing/BillingDashboard';
import SubscriptionManagement from './billing/SubscriptionManagement';
import PaymentProcessing from './billing/PaymentProcessing';
import InvoiceManagement from './billing/InvoiceManagement';
import BillingReports from './billing/BillingReports';
import BillingSettings from './billing/BillingSettings';

const UnifiedBilling = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Sync activeTab with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'dashboard';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  const tabs = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: CreditCard,
      description: 'Billing overview and metrics'
    },
    { 
      id: 'subscriptions', 
      label: 'Subscriptions', 
      icon: FileText,
      description: 'Manage subscriptions'
    },
    { 
      id: 'payments', 
      label: 'Payments', 
      icon: DollarSign,
      description: 'Payment processing'
    },
    { 
      id: 'invoices', 
      label: 'Invoices', 
      icon: FileText,
      description: 'Invoice management'
    },
    { 
      id: 'reports', 
      label: 'Reports', 
      icon: BarChart3,
      description: 'Billing reports and analytics'
    },
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: Settings,
      description: 'Billing configuration'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <CreditCard className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Billing Management</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Manage subscriptions, payments, invoices, and billing settings
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/super-admin/dashboard')}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-1 overflow-x-auto px-4" aria-label="Billing Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300'
                    }`}
                    title={tab.description}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'dashboard' && (
              <div>
                <BillingDashboard />
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div>
                <SubscriptionManagement />
              </div>
            )}

            {activeTab === 'payments' && (
              <div>
                <PaymentProcessing />
              </div>
            )}

            {activeTab === 'invoices' && (
              <div>
                <InvoiceManagement />
              </div>
            )}

            {activeTab === 'reports' && (
              <div>
                <BillingReports />
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <BillingSettings />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedBilling;

